import { Vault } from "../../libs/storage.js";
import type { EventCollector } from "../EventCollector";
import { SiteGeo } from "../type/SiteGeo";
import {
  VslAssignment,
  VslProgressRecord,
  VslServedContext,
  VslTargetingPayload,
  ServeVslResponse,
} from "../type/VslServing";
import { fetchFirstParty } from "./FirstPartyFetch";

export { VslServing };

const VSL_ASSIGNMENT_PREFIX = "vsl-assignment:";
const VSL_PROGRESS_PREFIX = "vsl-progress:";
const VSL_PROGRESS_TTL = "60d";
const WEBINAR_POSITION_PREFIX = "webinar-pos:";
const VSL_GEO_TIMEOUT_MS = 400;
const VSL_SERVE_TIMEOUT_MS = 8000;

// String literal en vez de importar el enum DataType: un import de valor
// desde EventCollector crearía un ciclo de módulos (EventCollector ya importa
// esta clase). El tipo del parámetro lo garantiza igualmente.
const WEBANALYTICS = "webanalytics" as Parameters<EventCollector["do"]>[0];

class VslServing {
  private _eventCollector: EventCollector;
  // Asignaciones servidas en ESTA carga de página, por scope de instancia.
  // Es la fuente de `vsl_serving.assignments` que WebAnalyticsEventStructure
  // adjunta a todos los eventos: así deciles/ended quedan atribuidos a la
  // variante sin tocar el plugin webinar (serializado en el contenido).
  private _servedThisPage: Map<string, VslServedContext> = new Map();

  constructor(eventCollector: EventCollector) {
    this._eventCollector = eventCollector;
  }

  get servedAssignments(): VslServedContext[] {
    return Array.from(this._servedThisPage.values());
  }

  // Progreso de vídeo por grupo (fast-follow de TTH-1610): persiste en el
  // navegador el contexto de variante + decil máximo por grupo VSL, para que
  // el evento de agenda (`onScheduledEvent`) pueda emitirse con contexto
  // completo también desde la página de gracias, donde no hay asignaciones
  // vivas (`vsl_serving` iría vacío). Lo llama EventCollector.do() con cada
  // evento webanalytics; solo reacciona a los de VSL/vídeo.
  recordVideoProgress = (eventName: string): void => {
    try {
      const progress = this._progressForEvent(eventName);
      if (progress === undefined) {
        return;
      }
      const served = this.servedAssignments;
      // Los eventos de vídeo del plugin no llevan instance id: con DOS
      // vídeos distintos servidos en la página no se puede saber a cuál
      // pertenece el decil — mejor hueco honesto ("Sin datos de vídeo" en
      // el informe) que atribuirlo al equivocado. onVslServed (progress
      // null) sí se registra siempre: cada asignación es un hecho propio.
      if (progress !== null && served.length > 1) {
        const distinctVideos = new Set(
          served.map((s) => s.vsl_group_id + ":" + s.vsl_id),
        );
        if (distinctVideos.size > 1) {
          return;
        }
      }
      for (const assignment of served) {
        const key = VSL_PROGRESS_PREFIX + "g" + assignment.vsl_group_id;
        const previous = Vault.get(key) as VslProgressRecord | false;
        // Si el grupo cambió de variante entre visitas (re-bucket), el
        // progreso anterior pertenece a OTRO vídeo: se reinicia.
        const sameVsl = previous && previous.vsl_id === assignment.vsl_id;
        const previousMax =
          sameVsl && typeof previous.max_decile === "number"
            ? previous.max_decile
            : null;
        const maxDecile =
          progress === null
            ? previousMax
            : previousMax === null
              ? progress
              : Math.max(previousMax, progress);
        const record: VslProgressRecord = {
          vsl_group_id: assignment.vsl_group_id,
          vsl_id: assignment.vsl_id,
          group_snapshot_id: assignment.group_snapshot_id,
          snapshot_version: assignment.snapshot_version,
          is_fallback: assignment.is_fallback,
          max_decile: maxDecile,
          updated_at: new Date().toISOString(),
        };
        Vault.set(key, record, { expires: VSL_PROGRESS_TTL });
      }
    } catch (e) {
      // el registro de progreso jamás interfiere con la analítica
    }
  };

  // Todos los registros de progreso no expirados del navegador (Vault.get
  // descarta los caducados). Asume localStorage, como los scheduled events.
  getVslProgressAll = (): VslProgressRecord[] => {
    const results: VslProgressRecord[] = [];
    try {
      if (typeof localStorage === "undefined") {
        return results;
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || key.indexOf(VSL_PROGRESS_PREFIX) !== 0) {
          continue;
        }
        const value = Vault.get(key);
        if (
          value &&
          typeof value === "object" &&
          typeof (value as VslProgressRecord).vsl_group_id === "number"
        ) {
          results.push(value as VslProgressRecord);
        }
      }
    } catch (e) {
      // sin storage no hay progreso que devolver
    }
    return results;
  };

  // null = solo asegurar el registro (asignación servida sin arranque);
  // undefined = el evento no es de VSL/vídeo, ignorar.
  private _progressForEvent = (
    eventName: string,
  ): number | null | undefined => {
    if (eventName === "onVslServed") {
      return null;
    }
    if (eventName === "onWebinarVideoStart") {
      return 0;
    }
    if (eventName === "onWebinarVideoEnded") {
      return 10;
    }
    const decileMatch = /^onWebinarVideoViewedDecile(10|[1-9])$/.exec(
      eventName,
    );
    return decileMatch ? Number(decileMatch[1]) : undefined;
  };

  // La analítica jamás puede romper el serving: fire-and-forget con doble
  // guard (throw síncrono y rejection de la promesa de do()).
  private _emitVslAnalytics = (
    eventName: string,
    vslServing?: Record<string, unknown>,
  ): void => {
    try {
      void this._eventCollector
        .do(WEBANALYTICS, {
          event_name: eventName,
          ...(vslServing ? { vsl_serving: vslServing } : {}),
        })
        .catch((e: unknown) => console.log(e));
    } catch (e) {
      console.log(e);
    }
  };

  private _resolveWebinarScope = (
    instanceId: string,
    vslGroupId: number,
  ): string => {
    const pageKey =
      (window.location.pathname || "/")
        .replace(/\/+/g, "/")
        .replace(/\/+$/, "") || "/";
    if (instanceId) {
      return pageKey + "::" + instanceId;
    }
    const groupPart = vslGroupId > 0 ? String(vslGroupId) : "0";
    return pageKey + "::g" + groupPart;
  };

  saveVslAssignment = (
    instanceId: string,
    vslGroupId: number,
    assignment: VslAssignment,
  ): boolean => {
    try {
      const scope = this._resolveWebinarScope(instanceId, vslGroupId);
      localStorage.setItem(
        VSL_ASSIGNMENT_PREFIX + scope,
        JSON.stringify(assignment),
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  getVslAssignment = (
    instanceId: string,
    vslGroupId: number,
  ): VslAssignment | null => {
    try {
      const scope = this._resolveWebinarScope(instanceId, vslGroupId);
      const raw = localStorage.getItem(VSL_ASSIGNMENT_PREFIX + scope);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.groupSnapshotId === "number" &&
        typeof parsed.vslId === "number"
      ) {
        return {
          groupSnapshotId: parsed.groupSnapshotId,
          vslId: parsed.vslId,
          isFallback: !!parsed.isFallback,
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  saveWebinarPosition = (
    instanceId: string,
    vslGroupId: number,
    seconds: number,
  ): boolean => {
    try {
      const scope = this._resolveWebinarScope(instanceId, vslGroupId);
      localStorage.setItem(WEBINAR_POSITION_PREFIX + scope, String(seconds));
      return true;
    } catch (e) {
      return false;
    }
  };

  getWebinarPosition = (
    instanceId: string,
    vslGroupId: number,
  ): number | null => {
    try {
      const scope = this._resolveWebinarScope(instanceId, vslGroupId);
      const raw = localStorage.getItem(WEBINAR_POSITION_PREFIX + scope);
      if (!raw) {
        return null;
      }
      const seconds = parseFloat(raw);
      return isNaN(seconds) ? null : seconds;
    } catch (e) {
      return null;
    }
  };

  // 'mobile' | 'tablet' | 'desktop' a partir del user agent.
  private _deriveDevice = (ua: string): string => {
    const s = (ua || "").toLowerCase();
    if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/.test(s)) {
      return "tablet";
    }
    if (
      /mobi|iphone|ipod|android.*mobile|windows phone|blackberry|iemobile|opera mini/.test(
        s,
      )
    ) {
      return "mobile";
    }
    return "desktop";
  };

  private _resolveVslGeo = (): Promise<SiteGeo | null> => {
    return Promise.race([
      this._eventCollector.geo.catch(() => null),
      new Promise<SiteGeo | null>((resolve) => {
        setTimeout(() => resolve(null), VSL_GEO_TIMEOUT_MS);
      }),
    ]);
  };

  serveVsl = async (
    vslGroupId: number,
    instanceId: string,
  ): Promise<ServeVslResponse | null> => {
    const emitError = (reason: string, extra: Record<string, unknown> = {}) => {
      try {
        this._eventCollector.emitCustomEvent("onServeVslError", {
          vslGroupId,
          instanceId,
          reason,
          ...extra,
        });
      } catch (e) {
        console.log(e);
      }
      // Espejo webanalytics del error (TTH-1608): solo ids + reason — nunca
      // los payloads crudos de `extra`, que van a GTM para depurar pero no
      // caben en una fila de analítica.
      this._emitVslAnalytics("onVslServeError", {
        error: {
          vsl_group_id: vslGroupId,
          instance_id: instanceId || "",
          reason,
        },
      });
    };

    try {
      if (!Number.isInteger(vslGroupId) || vslGroupId <= 0) {
        emitError("invalid vslGroupId");
        return null;
      }
      const endpoint =
        this._eventCollector.config?.CFG_ENDPOINTS?.VslServingEndpoint;
      if (!endpoint) {
        emitError("missing VslServingEndpoint");
        return null;
      }

      const asNullableString = (v: unknown): string | null =>
        typeof v === "string" && v.length > 0 ? v : null;

      let utm: {
        utm_campaign?: string | boolean;
        utm_source?: string | boolean;
        utm_medium?: string | boolean;
        utm_content?: string | boolean;
        utm_id?: string | boolean;
        utm_term?: string | boolean;
      } = {};
      try {
        utm = this._eventCollector.tracking?.getAdditionalDataToMessage() || {};
      } catch (e) {
        utm = {};
      }

      let userInfo: {
        user_browser?: string;
        user_browser_version?: string;
        user_os?: string;
        user_device?: string;
      } = {};
      try {
        userInfo = this._eventCollector.user?.getAdditionalDataToMessage() || {};
      } catch (e) {
        userInfo = {};
      }

      let userEmail: string | null = null;
      try {
        userEmail = asNullableString(
          this._eventCollector.user?.getUserFormData()?.email,
        );
      } catch (e) {
        userEmail = null;
      }

      const geo = await this._resolveVslGeo();

      let protoC: string | null = null;
      try {
        protoC = asNullableString(
          new URLSearchParams(window.location.search).get("proto_c"),
        );
      } catch (e) {
        protoC = null;
      }

      const targetingPayload: VslTargetingPayload = {
        device: this._deriveDevice(navigator.userAgent),
        language: navigator.language || "en",
        geoCountry: asNullableString(geo && geo.country),
        geoRegion: asNullableString(geo && geo.region),
        // Omitida sin proto_c (ver el tipo): compat con la Lambda ya
        // desplegada durante el rollout del SDK.
        ...(protoC !== null ? { custom: protoC } : {}),
        utmCampaign: asNullableString(utm.utm_campaign),
        utmSource: asNullableString(utm.utm_source),
        userBrowser: asNullableString(userInfo.user_browser),
        userBrowserVersion: asNullableString(userInfo.user_browser_version),
        userDevice: asNullableString(userInfo.user_device),
        userEmail,
        userOs: asNullableString(userInfo.user_os),
        utmMedium: asNullableString(utm.utm_medium),
        utmContent: asNullableString(utm.utm_content),
        utmId: asNullableString(utm.utm_id),
        utmTerm: asNullableString(utm.utm_term),
      };

      const request = {
        vslGroupId,
        currentAssignment: this.getVslAssignment(instanceId, vslGroupId),
        targetingPayload,
      };

      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      const timer = setTimeout(() => {
        if (controller) {
          controller.abort();
        }
      }, VSL_SERVE_TIMEOUT_MS);

      const response = await fetchFirstParty(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal: controller ? controller.signal : undefined,
        keepalive: true,
      });
      clearTimeout(timer);

      if (!response.ok) {
        const rawResponse = await response.text();
        emitError("serve-vsl response not ok", { rawResponse });
        return null;
      }

      const body: ServeVslResponse = await response.json();
      if (!body || !body.assignment || body.groupSnapshotId == null) {
        emitError("serve-vsl response without assignment", { response: body });
        return null;
      }

      this.saveVslAssignment(instanceId, vslGroupId, {
        groupSnapshotId: body.groupSnapshotId,
        vslId: body.assignment.vslId,
        isFallback: body.assignment.isFallback,
      });
      // TTH-1608: registrar la asignación servida y persistirla como evento
      // de analítica. Hasta ahora la decisión de qué VSL se sirve solo iba a
      // GTM/DOM (onServeVslSuccess, intacto abajo) y era inconsultable:
      // imposible saber la variante ganadora. snapshot_version y rebucketed
      // vienen de la Lambda serve-vsl y hasta hoy se descartaban.
      this._servedThisPage.set(
        this._resolveWebinarScope(instanceId, vslGroupId),
        {
          instance_id: instanceId || "",
          vsl_group_id: vslGroupId,
          vsl_id: body.assignment.vslId,
          group_snapshot_id: body.groupSnapshotId,
          snapshot_version: body.snapshotVersion ?? null,
          is_fallback: Boolean(body.assignment.isFallback),
          rebucketed: Boolean(body.rebucketed),
          tree_order_index: body.treeOrderIndex ?? null,
        },
      );
      this._emitVslAnalytics("onVslServed");
      this._eventCollector.emitCustomEvent("onServeVslSuccess", {
        vslGroupId,
        instanceId,
        response: body,
      });
      return body;
    } catch (error) {
      console.log(error);
      emitError("serve-vsl request failed");
      return null;
    }
  };
}
